/**
 * Gantt Chart — pure model (Gantt Chart FSD).
 *
 * Everything in this file is a pure function over the Product Development
 * dashboard payload: status normalisation, the rollup precedence, the row tree,
 * the level x status colour matrix and the timeline maths. No React, no IO — so
 * the rules the FSD states can be read (and tested) in one place, separately
 * from how they are drawn.
 *
 * DATA SOURCE. The FSD puts "the upstream data feed that populates the chart's
 * data source" out of scope, and the dashboard's own /fetchProjectDashboard
 * response already carries the exact hierarchy the chart needs —
 * project -> campaignsList -> campaignStageList -> batchesList, with
 * batchStartDate / batchEndDate / statusDescription per batch. So the chart is
 * built from that feed rather than from a new endpoint of its own.
 *
 * THREE FSD RULES ARE DELIBERATELY NOT IMPLEMENTED LITERALLY, because they
 * encode workarounds from the prototype the FSD was written against, and
 * following them here would make the product worse:
 *
 *  • BR-05 (project display label from the dominant 3-letter batch-code prefix,
 *    looked up in a "master PROJECT label lookup", falling back to the raw
 *    campaign ID). The prototype's data source had no project name, so it had
 *    to infer one from batch IDs. The real feed carries the project's own
 *    composed display label (`projectDisplayId`, e.g. "1462139-LTSS-BSQ"), so
 *    that is used directly — see projectLabelOf. A hard-coded prefix->name
 *    table would go stale on every new project, which is precisely the failure
 *    mode BR-05's own business rule warns about.
 *
 *  • Section A's hard-coded CMO ('Sai Life Sciences') and Site ('HYD / Unit II'
 *    default, 'Bidar / Unit IV' alternate). Those are one customer's values.
 *    The screen loads its CMO and Site options from the same live endpoints the
 *    dashboard filters use; see ./api.
 *
 *  • BR-12 ("changing Site does not re-query REAL_DATA in this release, single
 *    active data source"). That describes the prototype's one static dataset.
 *    The dashboard feed genuinely is scopeable by CMO and Site, so the chart
 *    re-queries — see the portfolio effect in ./index.jsx. Leaving the
 *    selectors inert would make them lie about what is on screen.
 */

// ── Status vocabulary ───────────────────────────────────────────────────────
// The FSD works in five statuses. The ledger records a wider, level-specific
// vocabulary, so raw values are normalised onto those five here.
//
// Sources of raw values (all observed in this codebase):
//   project.projectStatus   Completed | In-Progress | Created | Initiated | On-Hold
//   campaign.campaignStatus Completed | Ongoing | Inactive | Pending | Delayed
//   batch.statusDescription Closed | GRN Generated | New | Released | Ongoing | Inactive
//
// "Delayed" maps to On Hold rather than In-Progress on purpose: On Hold is the
// status the FSD rolls up ahead of everything else (BR-04), which is the right
// behaviour for a row that needs attention. Flagged in the handover notes as a
// mapping worth confirming with the business.
export const STATUS = {
  COMPLETED: 'Completed',
  IN_PROGRESS: 'In-Progress',
  PLANNED: 'Planned',
  ON_HOLD: 'On Hold',
  CANCELLED: 'Cancelled',
};

// Appendix — Status Colour Mapping. `color` is the flat status fill used for a
// row whose status falls outside the hierarchy matrix (On Hold, Cancelled).
export const STATUS_COLOURS = {
  [STATUS.COMPLETED]: { fill: '#329429', text: '#1E6B1A' },
  [STATUS.IN_PROGRESS]: { fill: '#286ACF', text: '#1F56AB' },
  [STATUS.PLANNED]: { fill: '#9A9AA2', text: '#5F5F68' },
  [STATUS.ON_HOLD]: { fill: '#6B2A7B', text: '#5B2369' },
  [STATUS.CANCELLED]: { fill: '#9A9AA2', text: '#6E6E76' },
};

// The four status filter chips (FSD D). Cancelled is a reference status only —
// "not a filter chip" per the appendix — so it is not listed here.
export const FILTER_STATUSES = [
  STATUS.COMPLETED,
  STATUS.IN_PROGRESS,
  STATUS.PLANNED,
  STATUS.ON_HOLD,
];

export const ROW_KIND = {
  PROJECT: 'project',
  CAMPAIGN: 'campaign',
  STAGE: 'stage',
  BATCH: 'batch',
};

// Appendix — Hierarchy x Status Bar Colour Matrix. Only the three statuses the
// matrix defines appear here; anything else falls back to the flat status
// colour (BR-10).
export const HIERARCHY_COLOURS = {
  [ROW_KIND.PROJECT]: {
    [STATUS.COMPLETED]: '#0F5024',
    [STATUS.IN_PROGRESS]: '#14346F',
    [STATUS.PLANNED]: '#4B4B54',
  },
  [ROW_KIND.CAMPAIGN]: {
    [STATUS.COMPLETED]: '#1F7B36',
    [STATUS.IN_PROGRESS]: '#286ACF',
    [STATUS.PLANNED]: '#71717A',
  },
  [ROW_KIND.STAGE]: {
    [STATUS.COMPLETED]: '#4FA862',
    [STATUS.IN_PROGRESS]: '#5C97E0',
    [STATUS.PLANNED]: '#9A9AA2',
  },
  [ROW_KIND.BATCH]: {
    [STATUS.COMPLETED]: '#9AD0A4',
    [STATUS.IN_PROGRESS]: '#AECDF2',
    [STATUS.PLANNED]: '#D3D3DA',
  },
};

// Raw batch status -> one of the five FSD statuses, by EXACT name.
//
// This was previously a list of prefix regexes, which classified a status by
// whatever word it happened to start with. Two of them were plainly wrong
// against the statuses the platform actually defines: /^grn/ put "GRN Pending"
// under Completed, and "Under Production" - the status that means the batch is
// being made right now - matched nothing and fell through to Planned. 36 of the
// 41 real statuses landed on Planned, so nearly every bar rendered grey and the
// rollups and filter chips were meaningless.
//
// The names below are taken from the two status masters the platform ships
// (mosymPhonyDsBatchStatuses.js) plus the vocabulary the Executive Summary FSD
// section 7.1 enumerates. Nothing here is invented: a status not on this list is
// not classified by guesswork, it falls to Planned (the FSD default) and is
// reported by unmappedStatuses() so the gap is visible rather than silent.
//
// Where a status sits between buckets, the split follows the Executive Summary
// FSD's own roll-up: Closed is the only terminal state, so everything earlier in
// the lifecycle is still live - In-Progress once work has started, Planned
// before it. THREE assignments are judgement rather than evidence and are
// marked (*) below; they need business sign-off:
//   Quarantine / CoA Rejected / Resubmit*  -> On Hold   (paused, awaiting action)
//   Rejected / Rejected by CMO             -> Cancelled (withdrawn)
const STATUS_BY_NAME = new Map();
const register = (status, names) =>
  names.forEach((n) => STATUS_BY_NAME.set(statusKey(n), status));

// Letters and digits only, so casing, spacing and punctuation in the source
// ("CoA Approved" / "COA approved", "On Hold" / "On-Hold") cannot cause a miss.
function statusKey(value) {
  return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// The five FSD status names themselves, in case the feed already speaks them.
register(STATUS.COMPLETED, ['Completed']);
register(STATUS.IN_PROGRESS, ['In-Progress']);
register(STATUS.PLANNED, ['Planned']);
register(STATUS.ON_HOLD, ['On Hold']);
register(STATUS.CANCELLED, ['Cancelled']);

// Closed is the only terminal state in the batch lifecycle.
register(STATUS.COMPLETED, ['Closed']);

// Work has started and has not closed out.
register(STATUS.IN_PROGRESS, [
  'In Progress', 'Under Production', 'Production Complete',
  'QC Released', 'CMO QC Released', 'Awaiting QC/QA', 'Repeat QC/QA',
  'Awaiting CoA Approval', 'CoA Approved', 'Approved',
  'Ready for Dispatch', 'Dispatch', 'Shipment Authorized',
  'Awaiting GRN', 'GRN Pending', 'GRN Generated', 'Goods Received',
  'Delivery Complete', 'STO/BD Generated',
  'E-Invoice Uploaded', 'E-Way Bill Uploaded',
]);

// Scheduled or awaiting kick-off; no production work yet.
register(STATUS.PLANNED, [
  'Scheduled', 'Created', 'New PO', 'Open', 'Pending', 'Confirmed',
  'Shared with CMO', 'Awaiting Approval', 'STO/BD Pending',
  'Under Confirmation', 'For Confirmation', 'Preparing for Production',
  'Batch Record Created', 'Ready for Production',
  'Changed', 'Changes under review', 'Status Not Found',
]);

// (*) Paused awaiting someone to act - sign-off pending.
register(STATUS.ON_HOLD, [
  'Quarantine', 'CoA Rejected',
  'Resubmit CoA', 'Resubmit Consumption Details', 'Resubmit Record',
]);

// (*) Withdrawn from the plan - sign-off pending.
register(STATUS.CANCELLED, ['Rejected', 'Rejected by CMO']);

/**
 * Raw status text -> one of the five FSD statuses. Anything unrecognised (or
 * blank) becomes Planned, which is also the rollup default (BR-04) - an unknown
 * status must never masquerade as Completed, since that would quietly tell the
 * reviewer work has closed out when nothing is known about it.
 */
export const normalizeStatus = (raw) => {
  const key = statusKey(raw);
  if (!key) return STATUS.PLANNED;
  return STATUS_BY_NAME.get(key) || STATUS.PLANNED;
};

/**
 * The distinct raw statuses in this data set that the table above does not
 * know. Every one of them is being drawn as Planned, so a non-empty result
 * means the chart is understating real work - surfaced rather than swallowed.
 */
export const unmappedStatuses = (projects) => {
  const seen = new Set();
  (projects || []).forEach((project) => {
    const campaigns = project.projectDetails?.campaigns
      || project.campaignsList || project.campaigns || [];
    campaigns.forEach((campaign) => {
      (campaign.stages || campaign.campaignStageList || []).forEach((stage) => {
        (stage.batches || stage.batchesList || []).forEach((batch) => {
          const raw = batch.batchStatus || batch.statusDescription;
          const key = statusKey(raw);
          if (key && !STATUS_BY_NAME.has(key)) seen.add(String(raw).trim());
        });
      });
    });
  });
  return [...seen].sort();
};

/**
 * Rollup precedence (FSD L, BR-04), strictly:
 *   any child On Hold      -> On Hold
 *   else any In-Progress   -> In-Progress
 *   else all Completed     -> Completed
 *   else                   -> Planned
 *
 * Cancelled children are IGNORED for the "all Completed" test — a plan whose
 * remaining work is all done should read Completed even if one batch was
 * withdrawn. A row whose children are ALL cancelled reads Cancelled.
 */
export const rollupStatus = (childStatuses) => {
  const list = (childStatuses || []).filter(Boolean);
  if (!list.length) return STATUS.PLANNED;
  if (list.includes(STATUS.ON_HOLD)) return STATUS.ON_HOLD;
  if (list.includes(STATUS.IN_PROGRESS)) return STATUS.IN_PROGRESS;
  const live = list.filter((s) => s !== STATUS.CANCELLED);
  if (!live.length) return STATUS.CANCELLED;
  if (live.every((s) => s === STATUS.COMPLETED)) return STATUS.COMPLETED;
  return STATUS.PLANNED;
};

/**
 * Bar colour for a row: the level x status matrix, falling back to the flat
 * status colour for a status the matrix does not define (BR-10).
 */
export const barColourOf = (kind, status) => {
  const byKind = HIERARCHY_COLOURS[kind];
  const matrix = byKind && byKind[status];
  if (matrix) return matrix;
  const flat = STATUS_COLOURS[status] || STATUS_COLOURS[STATUS.PLANNED];
  return flat.fill;
};

// ── dates ───────────────────────────────────────────────────────────────────
// Parsed to UTC midnight so a bar's position never shifts with the viewer's
// timezone (a date-only value parsed as local time lands on the previous day
// west of UTC, which visibly moves short bars by a whole day).
export const parseDate = (v) => {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return new Date(Date.UTC(+ymd[1], +ymd[2] - 1, +ymd[3]));
  const t = Date.parse(s);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

const DAY_MS = 86400000;
export const dayDiff = (a, b) => Math.round((b.getTime() - a.getTime()) / DAY_MS);
// Duration in days, inclusive of both endpoints (FSD J: "Duration (days,
// inclusive)").
export const inclusiveDays = (start, end) => dayDiff(start, end) + 1;

const utc = (y, m, d) => new Date(Date.UTC(y, m, d));
export const startOfMonth = (d) => utc(d.getUTCFullYear(), d.getUTCMonth(), 1);
export const endOfMonth = (d) => utc(d.getUTCFullYear(), d.getUTCMonth() + 1, 0);
export const startOfQuarter = (d) => utc(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1);
export const endOfQuarter = (d) => utc(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3 + 3, 0);
// Weeks start Monday, matching the ISO week the minor band labels.
export const startOfWeek = (d) => {
  const dow = d.getUTCDay(); // 0 = Sunday
  const back = dow === 0 ? 6 : dow - 1;
  return utc(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - back);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const shortDate = (d) => `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]}`;
export const longDate = (d) => `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;

// ── row tree ────────────────────────────────────────────────────────────────

// FSD definitions table: the Project row's display label. See the header note
// on why this is the feed's own label and not BR-05's batch-prefix inference.
//
// `projectDisplayId` FIRST — that is the dashboard's composed display value
// (productCode-sponsorProjectID-cmoProductCode, e.g. "1462139-LTSS-BSQ"), which
// is exactly the label the FSD quotes and what the dashboard's own Project ID
// column shows. `projectId` is NOT a label despite the name: dashboardAPI sets
// it to collabrixProjectID stringified (a numeric id), so preferring it renders
// every Project row as a bare number.
const projectLabelOf = (project) => {
  const label = [
    project.projectDisplayId,
    project.projectId,
    project.projectID,
    project.collabrixProjectID,
  ].find((v) => v != null && String(v).trim() !== '');
  return label == null ? 'Project' : String(label).trim();
};

const firstDefined = (...vals) => vals.find((v) => v != null && v !== '');

// A batch's dates. Falls back to the stage's, then the campaign's, so a batch
// with no dates of its own is still placed (rather than dropped) inside the
// span its parent already occupies.
const batchDates = (batch, stage, campaign) => {
  const start = parseDate(
    firstDefined(batch.batchStartDate, batch.startDate, stage.stageStartDate, campaign.campaignStartDate)
  );
  const end = parseDate(
    firstDefined(batch.batchEndDate, batch.endDate, stage.stageEndDate, campaign.campaignEndDate)
  );
  if (!start && !end) return null;
  // One-sided dates still describe a real (zero-length) position on the axis.
  return { start: start || end, end: end || start };
};

// Yield, where the feed carries it. The FSD's tooltip shows Target/Actual yield
// "when the underlying value is present", so absent values are simply omitted
// rather than shown as zero.
const yieldOf = (src) => {
  const target = firstDefined(src.productQuantityRequired, src.targetYield, src.plannedQuantity);
  const actual = firstDefined(src.actualProductQuantity, src.actualYield, src.producedQuantity);
  const uom = firstDefined(src.uom, src.uomName, 'Kg');
  const t = target == null ? null : Number(target);
  const a = actual == null ? null : Number(actual);
  return {
    target: Number.isFinite(t) ? t : null,
    actual: Number.isFinite(a) ? a : null,
    uom,
  };
};

// Span of a set of children: earliest start to latest end (FSD F).
const spanOf = (children) => {
  const withDates = children.filter((c) => c.start && c.end);
  if (!withDates.length) return { start: null, end: null };
  return {
    start: new Date(Math.min(...withDates.map((c) => c.start.getTime()))),
    end: new Date(Math.max(...withDates.map((c) => c.end.getTime()))),
  };
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Yield half of a summary row's sub-label (FSD F: "a sub-label with counts and
// yield, e.g. '3 campaigns · 42 batches' or '<actual> / <target> Kg'").
//
// Rendered only when a TARGET exists — without one there is nothing to read the
// value against. A missing actual shows as a dash rather than suppressing the
// whole label, so a row with a target but no recorded output still says so.
const yieldLabelOf = (y) => {
  if (!y || y.target == null) return null;
  const actual = y.actual == null ? '—' : y.actual;
  return `${actual} / ${y.target} ${y.uom}`;
};

// counts · yield, dropping whichever part has nothing behind it.
const subLabelOf = (counts, y) => [counts, yieldLabelOf(y)].filter(Boolean).join(' · ');

/**
 * Build the Project -> Campaign -> Stage -> Batch row tree from the dashboard's
 * project list.
 *
 * Projects are GROUPED BY DISPLAY LABEL (FSD F: "Rows are grouped by Project
 * (grouped by resolved display label)") — the same project can appear as more
 * than one dashboard row (one per site/PO), and the chart shows one Project row
 * for it.
 *
 * Every row carries: id, kind, name, status (raw for batches, rollup for
 * summaries), start, end, sublabel, children, and the identity a drill-down
 * needs (campaignId on campaign rows).
 */
export const buildRowTree = (projects) => {
  const byLabel = new Map();

  (projects || []).forEach((project) => {
    const label = projectLabelOf(project);
    if (!byLabel.has(label)) byLabel.set(label, []);
    byLabel.get(label).push(project);
  });

  const projectRows = [];

  byLabel.forEach((group, label) => {
    const campaignRows = [];

    group.forEach((project) => {
      const campaigns = project.projectDetails?.campaigns || project.campaignsList || project.campaigns || [];
      campaigns.forEach((campaign) => {
        const campaignId = String(firstDefined(campaign.campaignId, campaign.campaignID, '') || '');
        const stageRows = [];

        (campaign.stages || campaign.campaignStageList || []).forEach((stage) => {
          const stageCode = String(firstDefined(stage.stageId, stage.cmoStageID, stage.stageNumber, '') || '');
          const batchRows = [];

          (stage.batches || stage.batchesList || []).forEach((batch) => {
            const dates = batchDates(batch, stage, campaign);
            if (!dates) return; // nothing to place on a timeline
            const batchNumber = String(firstDefined(batch.batchNumber, batch.vendorBatchNumber, '') || '');
            batchRows.push({
              id: `b:${campaignId}:${stageCode}:${batchNumber || batch.batchId}`,
              kind: ROW_KIND.BATCH,
              name: batchNumber || String(batch.batchId || 'Batch'),
              status: normalizeStatus(firstDefined(batch.batchStatus, batch.statusDescription)),
              rawStatus: String(firstDefined(batch.batchStatus, batch.statusDescription) || ''),
              start: dates.start,
              end: dates.end,
              plannedStart: parseDate(firstDefined(batch.plannedStartDate, batch.committedStartDate)),
              plannedEnd: parseDate(firstDefined(batch.plannedEndDate, batch.committedEndDate)),
              yield: yieldOf(batch),
              campaignId,
              stage: stageCode,
              children: [],
            });
          });

          if (!batchRows.length) return;

          const span = spanOf(batchRows);
          const status = rollupStatus(batchRows.map((b) => b.status));
          const stageYield = yieldOf(stage);
          stageRows.push({
            id: `s:${campaignId}:${stageCode}`,
            kind: ROW_KIND.STAGE,
            name: stageCode ? `Stage ${stageCode}` : String(stage.stageName || 'Stage'),
            status,
            start: span.start,
            end: span.end,
            sublabel: subLabelOf(plural(batchRows.length, 'batch', 'batches'), stageYield),
            yield: stageYield,
            campaignId,
            stage: stageCode,
            children: batchRows,
          });
        });

        if (!stageRows.length) return;

        const span = spanOf(stageRows);
        const batchCount = stageRows.reduce((n, s) => n + s.children.length, 0);
        const campaignYield = yieldOf(campaign);
        campaignRows.push({
          id: `c:${campaignId}`,
          kind: ROW_KIND.CAMPAIGN,
          name: `Campaign ${campaignId}`,
          status: rollupStatus(stageRows.map((s) => s.status)),
          start: span.start,
          end: span.end,
          sublabel: subLabelOf(
            `${plural(stageRows.length, 'stage', 'stages')} · ${plural(batchCount, 'batch', 'batches')}`,
            campaignYield
          ),
          yield: campaignYield,
          // Only Campaign rows are drillable (BR-02).
          drillable: true,
          campaignId,
          projectLabel: label,
          children: stageRows,
        });
      });
    });

    if (!campaignRows.length) return;

    const span = spanOf(campaignRows);
    const batchCount = campaignRows.reduce(
      (n, c) => n + c.children.reduce((m, s) => m + s.children.length, 0),
      0
    );
    projectRows.push({
      id: `p:${label}`,
      kind: ROW_KIND.PROJECT,
      name: label,
      status: rollupStatus(campaignRows.map((c) => c.status)),
      start: span.start,
      end: span.end,
      sublabel: `${plural(campaignRows.length, 'campaign', 'campaigns')} · ${plural(batchCount, 'batch', 'batches')}`,
      children: campaignRows,
    });
  });

  // Stable order: by project label.
  projectRows.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  return projectRows;
};

/**
 * Status filtering (FSD D, BR-06). Filtering applies at Batch level and rolls
 * up: a Stage/Campaign/Project row survives only while it still has a visible
 * child. Summary statuses are RECOMPUTED from the surviving children, so a
 * filtered view's rollups describe what is actually on screen.
 */
export const filterTreeByStatus = (rows, activeStatuses) => {
  const active = new Set(activeStatuses || []);
  const keepBatch = (b) => active.has(b.status);

  const walk = (row) => {
    if (row.kind === ROW_KIND.BATCH) return keepBatch(row) ? row : null;
    const children = (row.children || []).map(walk).filter(Boolean);
    if (!children.length) return null;
    const span = spanOf(children);
    return {
      ...row,
      children,
      status: rollupStatus(children.map((c) => c.status)),
      start: span.start || row.start,
      end: span.end || row.end,
    };
  };

  return (rows || []).map(walk).filter(Boolean);
};

/**
 * Flatten the tree into the ordered visible row list, honouring per-row
 * expansion. `expanded` is a Set of row ids. Each emitted row carries its
 * `depth` (0 = Project) for indentation.
 */
export const flattenVisible = (rows, expanded) => {
  const out = [];
  const walk = (row, depth) => {
    out.push({ ...row, depth });
    const hasChildren = (row.children || []).length > 0;
    if (hasChildren && expanded.has(row.id)) {
      row.children.forEach((c) => walk(c, depth + 1));
    }
  };
  (rows || []).forEach((r) => walk(r, 0));
  return out;
};

// Every expandable row id in a tree (Project/Campaign/Stage — a Batch has no
// children), for Expand All (FSD E).
export const allExpandableIds = (rows) => {
  const ids = [];
  const walk = (row) => {
    if ((row.children || []).length > 0) {
      ids.push(row.id);
      row.children.forEach(walk);
    }
  };
  (rows || []).forEach(walk);
  return ids;
};

// ── timeline ────────────────────────────────────────────────────────────────

export const ZOOM = { QUARTER: 'Quarter', MONTH: 'Month', WEEK: 'Week' };

// Fixed pixels-per-day for the scrolling scales (BR-07). Portfolio + Quarter
// ignores these and auto-fits instead.
const PX_PER_DAY = {
  [ZOOM.QUARTER]: 1.6,
  [ZOOM.MONTH]: 4,
  [ZOOM.WEEK]: 16,
};

/**
 * Visible date window.
 *   Portfolio view — earliest start to latest end across all rows, rounded OUT
 *     to quarter boundaries (BR-07).
 *   Campaign view  — start of the campaign's start month through the end of its
 *     end month (FSD G).
 * Returns null when nothing in scope carries a date.
 */
export const dateWindowOf = (rows, { campaignRow = null } = {}) => {
  if (campaignRow) {
    if (!campaignRow.start || !campaignRow.end) return null;
    return { start: startOfMonth(campaignRow.start), end: endOfMonth(campaignRow.end) };
  }
  const span = spanOf(rows || []);
  if (!span.start || !span.end) return null;
  return { start: startOfQuarter(span.start), end: endOfQuarter(span.end) };
};

/**
 * Scale for the window: total days, pixels-per-day and total width.
 * `autoFit` (Portfolio + Quarter) solves px/day so the whole window fits the
 * available chart width; every other combination uses the fixed scale and lets
 * the chart scroll horizontally.
 */
export const scaleOf = (window, zoom, availableWidth, { autoFit = false } = {}) => {
  const days = Math.max(1, inclusiveDays(window.start, window.end));
  if (autoFit && availableWidth > 0) {
    const pxPerDay = availableWidth / days;
    return { days, pxPerDay, width: availableWidth };
  }
  const pxPerDay = PX_PER_DAY[zoom] || PX_PER_DAY[ZOOM.MONTH];
  return { days, pxPerDay, width: Math.round(days * pxPerDay) };
};

// x offset (px) of a date within the window.
export const xOf = (date, window, scale) =>
  Math.round(dayDiff(window.start, date) * scale.pxPerDay);

// A bar's {left, width} within the window, clamped to it so a row extending
// past the window edge is drawn to the edge rather than outside the chart.
export const barGeometry = (row, window, scale) => {
  if (!row.start || !row.end) return null;
  const from = row.start < window.start ? window.start : row.start;
  const to = row.end > window.end ? window.end : row.end;
  if (to < window.start || from > window.end) return null;
  const left = xOf(from, window, scale);
  // +1 day so a single-day row still has width.
  const width = Math.max(2, Math.round(inclusiveDays(from, to) * scale.pxPerDay));
  return { left, width };
};

/**
 * The two header bands for a zoom level (FSD C / H):
 *   Quarter -> major year,    minor quarter
 *   Month   -> major quarter, minor month
 *   Week    -> major month,   minor week
 * Each tick is { key, label, left, width }, in window order.
 */
export const headerBandsOf = (window, zoom, scale) => {
  const ticks = (stepStart, stepNext, labelOf) => {
    const out = [];
    let cursor = stepStart(window.start);
    let guard = 0;
    while (cursor <= window.end && guard < 2000) {
      guard += 1;
      const next = stepNext(cursor);
      const from = cursor < window.start ? window.start : cursor;
      const to = new Date(Math.min(next.getTime() - DAY_MS, window.end.getTime()));
      const left = xOf(from, window, scale);
      const width = Math.max(1, Math.round(inclusiveDays(from, to) * scale.pxPerDay));
      out.push({ key: from.toISOString(), label: labelOf(cursor), left, width });
      cursor = next;
    }
    return out;
  };

  const nextMonth = (d) => utc(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  const nextQuarter = (d) => utc(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3 + 3, 1);
  const nextYear = (d) => utc(d.getUTCFullYear() + 1, 0, 1);
  const nextWeek = (d) => utc(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 7);

  const quarterLabel = (d) => `Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
  const monthLabel = (d) => MONTHS[d.getUTCMonth()];
  const yearLabel = (d) => String(d.getUTCFullYear());
  const weekLabel = (d) => shortDate(d);

  if (zoom === ZOOM.WEEK) {
    return {
      major: ticks(startOfMonth, nextMonth, (d) => `${monthLabel(d)} ${d.getUTCFullYear()}`),
      minor: ticks(startOfWeek, nextWeek, weekLabel),
    };
  }
  if (zoom === ZOOM.MONTH) {
    return {
      major: ticks(startOfQuarter, nextQuarter, (d) => `${quarterLabel(d)} ${d.getUTCFullYear()}`),
      minor: ticks(startOfMonth, nextMonth, monthLabel),
    };
  }
  return {
    major: ticks((d) => utc(d.getUTCFullYear(), 0, 1), nextYear, yearLabel),
    minor: ticks(startOfQuarter, nextQuarter, quarterLabel),
  };
};

/**
 * Elapsed proportion for the In-Progress fill (FSD I): days from start to today
 * over total duration, clamped to 0..1. Returns null when the row is not
 * In-Progress — the fill is never drawn for any other status.
 */
export const elapsedFractionOf = (row, today = new Date()) => {
  if (row.status !== STATUS.IN_PROGRESS || !row.start || !row.end) return null;
  const now = parseDate(today) || today;
  const total = inclusiveDays(row.start, row.end);
  if (total <= 0) return null;
  const done = dayDiff(row.start, now) + 1;
  return Math.max(0, Math.min(1, done / total));
};

// x of today, or null when today falls outside the window — the marker is never
// clamped into range (BR-08).
export const todayXOf = (window, scale, today = new Date()) => {
  const now = parseDate(today) || today;
  if (now < window.start || now > window.end) return null;
  return xOf(now, window, scale);
};
